import { z } from 'zod';
export const MAX_ENGAGEMENT_MESSAGE_LENGTH = 4000;
export const EngagementContactRequestType = {
    ContactViaPhoneOrEmail: 'ContactViaPhoneOrEmail',
    ShowInterest: 'ShowInterest',
    ShowInterestWithMessage: 'ShowInterestWithMessage',
    OfferNonBindingPrice: 'OfferNonBindingPrice',
    OfferBindingPrice: 'OfferBindingPrice',
};
export const engagementContactRequestTypeSchema = z.enum([
    EngagementContactRequestType.ContactViaPhoneOrEmail,
    EngagementContactRequestType.ShowInterest,
    EngagementContactRequestType.ShowInterestWithMessage,
    EngagementContactRequestType.OfferNonBindingPrice,
    EngagementContactRequestType.OfferBindingPrice,
]);
export const CONTACT_REQUEST_TYPES_REQUIRING_MESSAGE = new Set([
    EngagementContactRequestType.ShowInterestWithMessage,
    EngagementContactRequestType.OfferNonBindingPrice,
    EngagementContactRequestType.OfferBindingPrice,
]);
export const CONTACT_REQUEST_TYPES_REQUIRING_PRICE = new Set([
    EngagementContactRequestType.OfferNonBindingPrice,
    EngagementContactRequestType.OfferBindingPrice,
]);
export const PRICE_BREAKDOWN_TOLERANCE_CHF = 0.02;
export function isContactRequestPriceBreakdownConsistent({ grossPriceChf, netPriceChf, vatRatePercent, }) {
    const expectedGross = netPriceChf * (1 + vatRatePercent / 100);
    return Math.abs(expectedGross - grossPriceChf) <= PRICE_BREAKDOWN_TOLERANCE_CHF;
}
export const PartnerEngagementStatus = {
    PendingAcceptance: 'PendingAcceptance',
    Accepted: 'Accepted',
    Declined: 'Declined',
};
export const partnerEngagementStatusSchema = z.enum([
    PartnerEngagementStatus.PendingAcceptance,
    PartnerEngagementStatus.Accepted,
    PartnerEngagementStatus.Declined,
]);
export const engagementDecisionResultSchema = z.object({
    uuid: z.uuid(),
    status: partnerEngagementStatusSchema,
});
export const postContactRequestSchema = z
    .object({
    type: engagementContactRequestTypeSchema,
    message: z.string().max(MAX_ENGAGEMENT_MESSAGE_LENGTH).optional(),
    grossPriceChf: z.number().positive().optional(),
    netPriceChf: z.number().positive().optional(),
    // Swiss VAT is at maximum 8.1%
    vatRatePercent: z.number().min(0).max(10).optional(),
})
    .superRefine((input, ctx) => {
    if (CONTACT_REQUEST_TYPES_REQUIRING_MESSAGE.has(input.type) && !input.message?.trim().length) {
        ctx.addIssue({
            code: 'custom',
            message: 'This contact request requires a message',
            path: ['message'],
        });
    }
    if (!CONTACT_REQUEST_TYPES_REQUIRING_PRICE.has(input.type)) {
        return;
    }
    if (input.grossPriceChf === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Price offers require a gross price',
            path: ['grossPriceChf'],
        });
    }
    if (input.netPriceChf === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Price offers require a net price',
            path: ['netPriceChf'],
        });
    }
    if (input.vatRatePercent === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Price offers require a VAT rate',
            path: ['vatRatePercent'],
        });
    }
    if (input.grossPriceChf !== undefined &&
        input.netPriceChf !== undefined &&
        input.vatRatePercent !== undefined &&
        !isContactRequestPriceBreakdownConsistent({
            grossPriceChf: input.grossPriceChf,
            netPriceChf: input.netPriceChf,
            vatRatePercent: input.vatRatePercent,
        })) {
        ctx.addIssue({
            code: 'custom',
            message: 'Gross price does not match net price and VAT rate',
            path: ['grossPriceChf'],
        });
    }
});
export const engagementContactRequestSchema = z.object({
    type: engagementContactRequestTypeSchema,
    messageText: z.string().nullable(),
    grossPriceChf: z.number().nullable(),
    requestedAt: z.iso.datetime(),
});
export const partnerEngagementSummarySchema = z.object({
    uuid: z.uuid(),
    status: partnerEngagementStatusSchema,
    contactRequest: engagementContactRequestSchema.nullable(),
});
export const postedContactRequestSchema = z.object({
    engagementUuid: z.uuid(),
});
export const myRequestsPartnerProfileSchema = z.object({
    companyName: z.string(),
    logoUrl: z.string().nullable(),
    postcode: z.string().nullable(),
    city: z.string().nullable(),
    description: z.string().nullable(),
    legalForm: z.string().nullable(),
    vatNumber: z.string().nullable(),
});
export const myRequestsEngagementSchema = z.object({
    engagementUuid: z.uuid(),
    status: partnerEngagementStatusSchema,
    partnerName: z.string(),
    partnerLogoUrl: z.string().nullable(),
    postcode: z.string().nullable(),
    city: z.string().nullable(),
    contactRequest: engagementContactRequestSchema.nullable(),
    createdAt: z.iso.datetime(),
    unreadCount: z.number().int().min(0),
});
